import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Form, Button, Row, Col } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Message from '../components/Message'
import Loader from '../components/Loader'
import FormContainer from '../components/FormContainer'
import { register } from '../slices/userSlice'
import ReCAPTCHA from "react-google-recaptcha"
import Meta from '../components/Meta'
import PolicyConsent from '../components/PolicyConsent'
import PasswordInput from '../components/PasswordInput'

// Added client-side normalization & validation helpers (mirrors backend rules subset)
const MAX_NAME_LEN = 50
const MAX_USERNAME_LEN = 30
const MAX_EMAIL_LEN = 254
const USERNAME_REGEX = /^[A-Za-z0-9_.-]+$/
const NAME_STRIP_REGEX = /[^A-Za-z'\-\s]/g
const CTRL_REGEX = /\p{Cc}/gu
const HTML_TAG_REGEX = /<[^>]+>/
const stripControl = (s='') => s.replace(CTRL_REGEX,'')
const collapseSpaces = (s='') => s.replace(/\s{2,}/g,' ')
const normalizeNFC = (s='') => { try { return s.normalize('NFC') } catch { return s } }
const capitalizeSimple = (s='') => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''
const formatPersonName = raw => capitalizeSimple(raw) // single-token capitalization
function sanitizeName(raw){
  let v = normalizeNFC(String(raw||''))
  v = stripControl(v).replace(NAME_STRIP_REGEX,'')
  v = collapseSpaces(v).trim()
  if (v.length > MAX_NAME_LEN) v = v.slice(0, MAX_NAME_LEN)
  v = formatPersonName(v)
  return v
}
function sanitizeUsername(raw){
  let v = normalizeNFC(String(raw||'')) // preserve case
  v = stripControl(v).trim()
  v = v.replace(/^[.-]+/, '').replace(/[.-]{2,}/g,'-') // plan: leading dots/hyphens removed, collapse repeats
  if (v.length > MAX_USERNAME_LEN) v = v.slice(0, MAX_USERNAME_LEN)
  return v
}
function sanitizeEmail(raw){
  let v = normalizeNFC(String(raw||''))
  v = stripControl(v).trim().toLowerCase()
  if (v.length > MAX_EMAIL_LEN) v = v.slice(0, MAX_EMAIL_LEN)
  return v
}
const hasHTML = s => HTML_TAG_REGEX.test(s||'')
const isValidPasswordLocal = pw => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(pw||'')

const RegisterScreen = ({ location, history }) => {
  // Store sanitized values directly (Option A)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState(null)
  const [grecaptcha, setGrecaptcha] = useState('')
  const [fieldErrors, setFieldErrors] = useState({
    firstName:'', lastName:'', username:'', email:'', password:'', confirmPassword:''
  })
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [viewedTerms, setViewedTerms] = useState(false)
  const [viewedPrivacy, setViewedPrivacy] = useState(false)

  function onChange(value) {
    setGrecaptcha(value)
  }

  const dispatch = useDispatch()

  // Use the RTK user slice
  const { loading, error, userRegister, userInfo } = useSelector((state) => state.user)

  // Field-level validation aggregator
  const validate = (vals) => {
    const errs = { firstName:'', lastName:'', username:'', email:'', password:'', confirmPassword:'' }
    const { firstName, lastName, username, email, password, confirmPassword } = vals
    if (!firstName) errs.firstName = 'First name required'
    else if (hasHTML(firstName)) errs.firstName = 'No HTML allowed'
    else if (firstName.length > MAX_NAME_LEN) errs.firstName = 'Too long'

    if (!lastName) errs.lastName = 'Last name required'
    else if (hasHTML(lastName)) errs.lastName = 'No HTML allowed'
    else if (lastName.length > MAX_NAME_LEN) errs.lastName = 'Too long'

    if (!username) errs.username = 'Username required'
    else if (hasHTML(username)) errs.username = 'No HTML allowed'
    else if (username.length > MAX_USERNAME_LEN) errs.username = 'Too long'
    else if (!USERNAME_REGEX.test(username)) errs.username = 'Invalid characters'

    if (!email) errs.email = 'Email required'
    else if (hasHTML(email)) errs.email = 'No HTML allowed'
    else if (email.length > MAX_EMAIL_LEN) errs.email = 'Too long'
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) errs.email = 'Invalid email format'

    if (!password) errs.password = 'Password required'
    else if (!isValidPasswordLocal(password)) errs.password = 'Min 8 chars, upper, lower, digit, special'

    if (!confirmPassword) errs.confirmPassword = 'Confirm password'
    else if (password !== confirmPassword) errs.confirmPassword = 'Passwords must match'

    return errs
  }

  // Handlers applying sanitization immediately
  const handleFirstName = (vRaw) => {
    const v = sanitizeName(vRaw)
    if (process.env.NODE_ENV !== 'production' && vRaw && vRaw !== v) console.log('firstName normalized:', vRaw, '->', v)
    setFirstName(v)
    setFieldErrors(prev => ({ ...prev, ...validate({ firstName:v, lastName, username, email, password, confirmPassword }) }))
  }
  const handleLastName = (vRaw) => {
    const v = sanitizeName(vRaw)
    if (process.env.NODE_ENV !== 'production' && vRaw && vRaw !== v) console.log('lastName normalized:', vRaw, '->', v)
    setLastName(v)
    setFieldErrors(prev => ({ ...prev, ...validate({ firstName, lastName:v, username, email, password, confirmPassword }) }))
  }
  const handleUsername = (vRaw) => {
    const v = sanitizeUsername(vRaw)
    if (process.env.NODE_ENV !== 'production' && vRaw && vRaw !== v) console.log('username normalized:', vRaw, '->', v)
    setUsername(v)
    setFieldErrors(prev => ({ ...prev, ...validate({ firstName, lastName, username:v, email, password, confirmPassword }) }))
  }
  const handleEmail = (vRaw) => {
    const v = sanitizeEmail(vRaw)
    if (process.env.NODE_ENV !== 'production' && vRaw && vRaw !== v) console.log('email normalized:', vRaw, '->', v)
    setEmail(v)
    setFieldErrors(prev => ({ ...prev, ...validate({ firstName, lastName, username, email:v, password, confirmPassword }) }))
  }
  const handlePassword = (v) => {
    setPassword(v)
    setFieldErrors(prev => ({ ...prev, ...validate({ firstName, lastName, username, email, password:v, confirmPassword }) }))
  }
  const handleConfirmPassword = (v) => {
    setConfirmPassword(v)
    setFieldErrors(prev => ({ ...prev, ...validate({ firstName, lastName, username, email, password, confirmPassword:v }) }))
  }

  // Re-run full validation when any essential field changes (ensures consistency)
  useEffect(() => {
    setFieldErrors(validate({ firstName, lastName, username, email, password, confirmPassword }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const redirect = location.search ? location.search.split('=')[1] : '/'

  // If already logged in, redirect
  useEffect(() => {
    if (userInfo) {
      history.push(redirect)
    }
  }, [history, userInfo, redirect])

  // On successful registration, redirect
  useEffect(() => {
    if (userRegister) {
      if (userRegister.URL) {
        history.push(userRegister.URL)
      } else {
        history.push(redirect)
      }
    }
  }, [userRegister, history, redirect])

  // Reset captcha on error
  useEffect(() => {
    if (error && window?.grecaptcha?.reset) {
      window.grecaptcha.reset()
    }
  }, [error])

  const submitHandler = (e) => {
    e.preventDefault()
    // Defensive re-sanitize
    const sFirst = sanitizeName(firstName)
    const sLast = sanitizeName(lastName)
    const sUser = sanitizeUsername(username)
    const sEmail = sanitizeEmail(email)

    const finalErrors = validate({ firstName:sFirst, lastName:sLast, username:sUser, email:sEmail, password, confirmPassword })
    setFieldErrors(finalErrors)
    const hasErr = Object.values(finalErrors).some(Boolean)
    if (hasErr) return
    if (!grecaptcha) {
      setMessage('Captcha required')
      return
    }
    if (!acceptedTerms || !acceptedPrivacy || !viewedTerms || !viewedPrivacy) {
      setMessage('You must view and accept the Terms & Conditions and Privacy Policy')
      return
    }
    setMessage(null)
    dispatch(
      register({ firstName: sFirst, lastName: sLast, username: sUser, email: sEmail, password, grecaptcha, acceptedTerms: true, acceptedPrivacy: true })
    )
  }

  const disableSubmit = !grecaptcha || Object.values(fieldErrors).some(Boolean) || !firstName || !lastName || !username || !email || !password || !confirmPassword || !acceptedTerms || !acceptedPrivacy || !viewedTerms || !viewedPrivacy

  return (
    <FormContainer>
      <Meta title='Register — lotustor.com' robots='noindex,follow' />
      <h1>Sign Up</h1>
      {message && <Message variant='danger'>{message}</Message>}
      {error && <Message variant='danger'>{error}</Message>}
      {loading && <Loader />}

      <Form onSubmit={submitHandler}>
        <Form.Group controlId='firstName'>
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter first name'
            value={firstName}
            onChange={(e) => handleFirstName(e.target.value)}
          />
          {fieldErrors.firstName && <small className='text-danger'>{fieldErrors.firstName}</small>}
        </Form.Group>

        <Form.Group controlId='lastName'>
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter last name'
            value={lastName}
            onChange={(e) => handleLastName(e.target.value)}
          />
          {fieldErrors.lastName && <small className='text-danger'>{fieldErrors.lastName}</small>}
        </Form.Group>

        <Form.Group controlId='username'>
          <Form.Label>Username</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter username'
            value={username}
            onChange={(e) => handleUsername(e.target.value)}
          />
          <Form.Text muted>Allowed: letters, numbers, underscore, dot, hyphen. Leading dots/hyphens removed; repeats collapsed.</Form.Text>
          {fieldErrors.username && <small className='text-danger d-block'>{fieldErrors.username}</small>}
        </Form.Group>

        <Form.Group controlId='email'>
          <Form.Label>Email Address</Form.Label>
            <Form.Control
              type='email'
              placeholder='Enter email'
              value={email}
              onChange={(e) => handleEmail(e.target.value)}
            />
          {fieldErrors.email && <small className='text-danger'>{fieldErrors.email}</small>}
        </Form.Group>

        <PasswordInput
          id='password'
          label='Password'
          value={password}
          onChange={(e) => handlePassword(e.target.value)}
          errorText={fieldErrors.password}
          autoComplete='new-password'
        />
        <Form.Text muted>Min 8 chars incl upper, lower, digit & special.</Form.Text>

        <PasswordInput
          id='confirmPassword'
          label='Confirm Password'
          value={confirmPassword}
          onChange={(e) => handleConfirmPassword(e.target.value)}
          errorText={fieldErrors.confirmPassword}
          autoComplete='new-password'
        />

        <Form.Group controlId='recaptcha'>
          <ReCAPTCHA
           sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}

            onChange={onChange}
          />
        </Form.Group>

        <PolicyConsent
          required
          initial={{ viewedPrivacy, acceptedPrivacy, viewedTerms, acceptedTerms }}
          onChange={({ acceptedTerms:at, acceptedPrivacy:ap, viewedTerms:vt, viewedPrivacy:vp }) => {
            setAcceptedTerms(!!at);
            setAcceptedPrivacy(!!ap);
            setViewedTerms(!!vt);
            setViewedPrivacy(!!vp);
          }}
        />

        <Button type='submit' variant='primary' disabled={disableSubmit}>
          Register
        </Button>
      </Form>

      <Row className='py-3'>
        <Col>
          Have an Account?{' '}
          <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>
            Login
          </Link>
        </Col>
      </Row>
    </FormContainer>
  )
}

export default RegisterScreen
